import React, { useEffect, useState } from "react";
import { Button, FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import BaseDataTable from "../../components/modules/BaseDataTable";
import CreateSegmentModal from "./CreateSegmentModal";
import Loader from "../../components/elements/Loader";
import api from "../../api";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { errorToaster, infoToaster } from "../../helpers/toasters";
import { setAgencyAdvertisers } from "../../redux/reducers/agency";
import { setSelectedSegmentIds } from "../../redux/reducers/campaignToCreate";

const columns = [
  { key: "segment_name", label: "Name", minWidth: 170 },
  { key: "tag", label: "Tag", minWidth: 170 },
  {
    key: "actions",
    label: "Actions",
    minWidth: 100,
  },
];

// function getSegmentTag(segmentId: number): string {
//   const segmentTag = `<!--BEGIN SEGMENT TAG, DO NOT REMOVE -->\n<img src=\"https://segment.prod.bidr.io/associate-segment?buzz_key=brandgen&segment_key=brandgen-${segmentId}&value=\" height=\"0\" width=\"0\">\n<!--END SEGMENT TAG, DO NOT REMOVE -->`;

//   return segmentTag;
// }

export default function RetargetinSegmentsPage({
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  handleNext,
}: {
  isAttemptingToGoNext?: boolean;
  setIsAttemptingToGoNext?: (isAttemptingToGoNext: boolean) => void;
  handleNext?: () => void;
}) {
  const [isCreateSegmentModalOpen, setIsCreateSegmentModalOpen] = useState(false);
  const [segments, setSegments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const [selectedAdvertiser, setSelectedAdvertiser] = useState<number | null>(null);

  const agencyId = useSelector((state: ReduxApplicationState) => state.agency.agencyId);
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);
  const selectedSegmentIds = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedSegmentIds);
  const selectedCampaignAdvertiser = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser,
  );

  useEffect(() => {
    dispatch(setAgencyAdvertisers(agencyId));
  }, [agencyId, dispatch]);

  const getSegments = async (advertiserId: number | null) => {
    try {
      if (!advertiserId) {
        setSegments([]);
      } else {
        setIsLoading(true);
        const { payload } = await api.getSegments(advertiserId);
        const segmentsList = Array.isArray(payload?.segments) ? payload.segments : [];

        setSegments(
          segmentsList.map((segment: any) => ({
            id: String(segment.id),
            segment_name: segment.name ?? "",
            tag: segment.pixelCode ?? "Processing...",
            actions: (
              <Button
                onClick={() => {
                  const text = segment.pixelCode ?? "";
                  navigator.clipboard.writeText(text);
                  infoToaster("Tag copied to clipboard", 2000);
                }}
                variant="outlined"
                color="primary"
                disabled={!segment.pixelCode}
              >
                Copy to Clipboard
              </Button>
            ),
          })),
        );
      }

      setIsLoading(false);
    } catch (error) {
      const err = error as any;
      throw new Error(err?.message ?? "Failed to load segments");
    }
  };

  useEffect(() => {
    if (!selectedAdvertiser && setIsAttemptingToGoNext) return;

    if (selectedAdvertiser) getSegments(selectedAdvertiser);
  }, [selectedAdvertiser, setIsAttemptingToGoNext]);

  useEffect(() => {
    if (isAttemptingToGoNext && setIsAttemptingToGoNext && handleNext) {
      if (selectedSegmentIds.length !== 0) {
        handleNext();
        setIsAttemptingToGoNext(false);
      } else {
        errorToaster("Please, select at least one segment", 5000);
        setIsAttemptingToGoNext(false);
      }
    }
  }, [
    dispatch,
    handleNext,
    isAttemptingToGoNext,
    setIsAttemptingToGoNext,
    selectedSegmentIds,
    selectedSegmentIds.length,
  ]);

  useEffect(() => {
    if (setIsAttemptingToGoNext && selectedCampaignAdvertiser) {
      setSelectedAdvertiser(selectedCampaignAdvertiser.adLibAdvertiserId);
    }
  }, [setIsAttemptingToGoNext, selectedCampaignAdvertiser]);

  useEffect(() => {
    if (advertisers.length > 0 && !selectedCampaignAdvertiser) {
      setSelectedAdvertiser(advertisers[0].adLibAdvertiserId);
    }
  }, [advertisers, selectedCampaignAdvertiser]);

  return (
    <div
      style={{
        marginTop: setIsAttemptingToGoNext ? "0.2rem" : "5rem",
        padding: "4rem",
      }}
    >
      <Button
        style={{
          marginBottom: "2rem",
          float: "right",
        }}
        variant="contained"
        color="primary"
        onClick={() => {
          setIsCreateSegmentModalOpen(true);
        }}
      >
        Create New Segment
      </Button>

      <CreateSegmentModal
        isOpen={isCreateSegmentModalOpen}
        shouldSetDefaultAdvertiser={!!setIsAttemptingToGoNext}
        handleClose={() => {
          setIsCreateSegmentModalOpen(false);

          setTimeout(() => {
            getSegments(selectedAdvertiser);
          }, 1000);
        }}
      />

      {!setIsAttemptingToGoNext && (
        <div
          style={{
            margin: "3rem 0",
            width: "20rem",
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Select Advertiser</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={selectedAdvertiser ?? ""}
              label="Advertiser"
              onChange={(e) => {
                const v = e.target.value;
                setSelectedAdvertiser(v === "" ? null : Number(v));
              }}
            >
              {advertisers.map((advertiser) => {
                return (
                  <MenuItem key={advertiser.adLibAdvertiserId ?? ""} value={advertiser.adLibAdvertiserId ?? ""}>
                    {advertiser.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      )}

      {isLoading && <Loader />}

      {!isLoading && (
        <BaseDataTable
          columns={columns}
          rows={segments}
          showCheckbox={!!setIsAttemptingToGoNext}
          selectedItems={selectedSegmentIds.map((x) => String(x))}
          toggleCheckbox={(id: string) => {
            if (selectedSegmentIds.includes(id)) {
              dispatch(setSelectedSegmentIds(selectedSegmentIds.filter((x) => x !== id)));
            } else {
              dispatch(setSelectedSegmentIds([...selectedSegmentIds, id]));
            }
          }}
        />
      )}
    </div>
  );
}
